import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { CheckCircle2, AlertCircle, Heart } from "lucide-react";

const SHIRT_SIZES = ["Youth S", "Youth M", "Youth L", "Adult S", "Adult M", "Adult L", "Adult XL", "Adult 2XL"];

function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob + "T00:00:00");
  if (isNaN(d)) return null;
  const ref = new Date("2026-09-05T00:00:00");
  let a = ref.getFullYear() - d.getFullYear();
  const m = ref.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < d.getDate())) a--;
  return a;
}

function eligibility(name, age, gender) {
  const n = (name || "").toLowerCase();
  const g = (gender || "").toLowerCase();
  if (age == null) return { ok: false, reason: "Enter date of birth first" };
  if (n.includes("women") || n.includes("girl"))
    return { ok: g === "female", reason: "Women's / Girls only" };
  if (n.includes("drop knee")) return { ok: true, reason: "All ages" };
  if (n.includes("menehune") || n.includes("under"))
    return { ok: age <= 13, reason: "Ages 13 & under" };
  if (n.includes("junior")) return { ok: age >= 14 && age <= 17, reason: "Ages 14–17" };
  if (n.includes("open men")) return { ok: age >= 18 && g === "male", reason: "Men 18 & over" };
  if (n.includes("master")) return { ok: age >= 40, reason: "Ages 40 & over" };
  return { ok: true, reason: "" };
}

const Field = ({ label, children, required }) => (
  <div className="mb-3">
    <label className="font-display text-[11px] uppercase tracking-widest text-gray-500 block mb-1">
      {label} {required && <span className="text-[#E63946]">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full brutal-2 p-3 font-display focus:outline-none bg-white";

export default function Register() {
  const [divisions, setDivisions] = useState([]);
  const [f, setF] = useState({
    first_name: "", last_name: "", dob: "", gender: "", email: "", phone: "",
    emergency_contact_name: "", emergency_contact_phone: "", shirt_size: "",
    division_ids: [], waiver_accepted: false, signature: "",
    guardian_name: "", guardian_relationship: "", guardian_phone: "",
    guardian_signature: "", guardian_consent: false, needs_sponsorship: false,
    sponsorship_reason: "",
    payment_method: "online",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState("form"); // form | verifying | success | failed | cash
  const [summary, setSummary] = useState(null);
  const [myHeats, setMyHeats] = useState([]);
  const [fund, setFund] = useState(null);
  const [bluevineUrl, setBluevineUrl] = useState("");
  const [paidClaimed, setPaidClaimed] = useState(false);

  useEffect(() => {
    api.divisions().then(setDivisions);
    api.sponsorshipStatus().then(setFund).catch(() => {});
    api.config().then((c) => setBluevineUrl(c.bluevine_url || "")).catch(() => {});
  }, []);

  const age = useMemo(() => ageFromDob(f.dob), [f.dob]);
  const isMinor = age != null && age < 18;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleDiv = (id) => {
    setF((p) => ({
      ...p,
      division_ids: p.division_ids.includes(id)
        ? p.division_ids.filter((x) => x !== id)
        : [...p.division_ids, id],
    }));
  };

  const submit = async () => {
    setError("");
    const req = ["first_name", "last_name", "dob", "gender", "email", "phone", "emergency_contact_name", "emergency_contact_phone"];
    for (const k of req) if (!String(f[k]).trim()) { setError("Please fill in all required fields."); window.scrollTo(0, 0); return; }
    if (!f.shirt_size) { setError("Please pick a T-shirt size."); window.scrollTo(0, 0); return; }
    if (f.division_ids.length === 0) { setError("Select at least one division to enter."); return; }
    if (!f.waiver_accepted) { setError("You must read and accept the waiver & release."); return; }
    if (!f.signature.trim()) { setError("Please type your signature."); return; }
    if (isMinor && !(f.guardian_name.trim() && f.guardian_phone.trim() && f.guardian_signature.trim() && f.guardian_consent)) {
      setError("Parent/guardian consent and signature are required for competitors under 18."); return;
    }
    const sponsorEligible = age != null && age <= 17;
    const requestingSponsorship = f.needs_sponsorship && sponsorEligible && !allWaitlist;
    if (requestingSponsorship && f.sponsorship_reason.trim().length < 20) {
      setError("Please share a short story (a few sentences) about why you're applying for a sponsorship."); return;
    }
    setSubmitting(true);
    setPaidClaimed(false);
    try {
      const payload = {
        ...f,
        needs_sponsorship: requestingSponsorship,
        sponsorship_reason: requestingSponsorship ? f.sponsorship_reason.trim() : "",
      };
      const res = await api.register(payload);
      const baseSummary = { full_name: res.full_name, divisions: res.divisions, amount_due: res.amount_due, id: res.id, waitlist_divisions: res.waitlist_divisions || [] };
      // All picked divisions were full → pure waitlist, nothing to pay
      if (res.waitlist) {
        setSummary(baseSummary);
        setPhase("waitlist");
        setSubmitting(false);
        window.scrollTo(0, 0);
        return;
      }
      // Sponsorship request → held for organizer approval, no payment taken
      if (res.sponsor_pending) {
        setSummary(baseSummary);
        setPhase("sponsor_requested");
        setSubmitting(false);
        window.scrollTo(0, 0);
        return;
      }
      // Pay in person / cash → holding area, no online payment
      if (f.payment_method === "cash" || res.cash) {
        setSummary({ ...baseSummary, needs_sponsorship: false });
        setPhase("cash");
        setSubmitting(false);
        window.scrollTo(0, 0);
        return;
      }
      // Pay online via Bluevine → held until an organizer confirms payment
      setSummary({ ...baseSummary, needs_sponsorship: false });
      setPhase("bluevine");
      setSubmitting(false);
      window.scrollTo(0, 0);
      return;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Something went wrong. Please try again.";
      setError(detail);
      // if a division filled up, refresh availability so FULL badges update live
      if (/full/i.test(detail)) api.divisions().then(setDivisions).catch(() => {});
      setSubmitting(false);
      window.scrollTo(0, 0);
    }
  };

  const selectedFull = divisions.filter((d) => f.division_ids.includes(d.id) && d.full);
  const payingCount = f.division_ids.filter((id) => { const d = divisions.find((x) => x.id === id); return d && !d.full; }).length;
  const allWaitlist = f.division_ids.length > 0 && payingCount === 0;
  const feeTotal = payingCount * 25;
  const requestingSponsorship = age != null && age <= 17 && f.needs_sponsorship && !allWaitlist;

  if (phase === "bluevine") {
    const amt = Number(summary?.amount_due ?? 0);
    return (
      <div className="max-w-lg mx-auto p-4 pt-10 pb-28" data-testid="register-bluevine">
        <div className="brutal p-6 text-center">
          <CheckCircle2 size={56} className="mx-auto text-[#FFB800] mb-3" />
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Almost There — Pay Online</h1>
          <p className="font-display mt-2 text-gray-700">
            {summary?.full_name ? `${summary.full_name}, we've` : "We've"} saved your entry. Complete your payment to lock in your spot.
          </p>
          {summary?.divisions && (
            <div className="brutal-2 mt-4 p-3 text-left">
              <div className="font-display text-xs uppercase tracking-widest text-gray-500">Entered divisions</div>
              <ul className="mt-1">
                {summary.divisions.map((d) => <li key={d} className="font-display font-bold uppercase">{d}</li>)}
              </ul>
            </div>
          )}
          {summary?.waitlist_divisions?.length > 0 && (
            <div className="brutal-2 bg-[#FFF3BF] p-3 mt-4 text-left" data-testid="confirm-waitlist-note">
              <div className="font-display text-xs uppercase tracking-widest text-[#B45309]">Also on the waitlist (no charge)</div>
              <ul className="mt-1">{summary.waitlist_divisions.map((d) => <li key={d} className="font-display font-bold uppercase">{d}</li>)}</ul>
              <p className="font-display text-[11px] mt-1">These are full — we'll place you if a spot opens.</p>
            </div>
          )}
          <div className="brutal-2 bg-black text-white p-4 mt-4">
            <div className="font-display text-xs uppercase tracking-widest text-gray-300">Amount to pay</div>
            <div className="font-score text-6xl text-[#FFD700]" data-testid="bluevine-amount">${amt.toFixed(2)}</div>
            <div className="font-display text-xs mt-1">{summary?.divisions?.length || 0} division{(summary?.divisions?.length || 0) === 1 ? "" : "s"} × $25</div>
          </div>
          {bluevineUrl ? (
            <a
              data-testid="bluevine-pay-link"
              href={bluevineUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full mt-4 brutal bg-[#E63946] text-white font-display font-black uppercase tracking-widest py-5 text-lg active:scale-[0.99] transition-transform"
            >
              Pay ${amt.toFixed(2)} Online →
            </a>
          ) : (
            <p className="font-display text-sm text-[#E63946] mt-4">Payment link unavailable — please contact the organizers.</p>
          )}
          <div className="brutal-2 bg-[#FFD700] p-3 mt-4 text-left" data-testid="bluevine-instructions">
            <p className="font-display text-sm font-bold uppercase tracking-wide">⚠️ Enter <span className="underline">${amt.toFixed(2)}</span> on the payment page</p>
            <p className="font-display text-sm mt-1">
              The payment page won't know your total, so please type <strong>${amt.toFixed(2)}</strong> yourself. Your spot is <strong>NOT confirmed until we receive your payment</strong> — once it lands, an organizer confirms your spot and places you in a heat. We also emailed you this payment link.
            </p>
          </div>
          {paidClaimed ? (
            <div className="brutal-2 bg-green-600 text-white p-4 mt-4 text-center" data-testid="bluevine-claimed">
              <p className="font-display font-black uppercase tracking-tight">Got it — organizers notified! ✅</p>
              <p className="font-display text-sm mt-1">We've let the organizers know you paid. They'll confirm your payment and place you in your heat. Mahalo!</p>
            </div>
          ) : (
            <button
              data-testid="bluevine-claim-btn"
              onClick={async () => { if (summary?.id) { await api.claimPayment(summary.id); setPaidClaimed(true); window.scrollTo(0, 0); } }}
              className="w-full mt-4 brutal bg-green-600 text-white font-display font-black uppercase tracking-widest py-4 active:scale-[0.99] transition-transform"
            >
              ✅ I've Completed My Payment
            </button>
          )}
          <p className="font-display text-[11px] text-gray-500 mt-2">Tap the button above <strong>after</strong> you finish paying so the organizers know to confirm your spot.</p>
          <button
            data-testid="register-another"
            onClick={() => { setSummary(null); setPhase("form"); setF({ ...f, first_name: "", last_name: "", dob: "", division_ids: [], waiver_accepted: false, signature: "", guardian_name: "", guardian_relationship: "", guardian_phone: "", guardian_signature: "", guardian_consent: false, needs_sponsorship: false }); }}
            className="mt-5 brutal bg-black text-white font-display font-black uppercase tracking-widest px-6 py-3"
          >
            Register Another Competitor
          </button>
        </div>
      </div>
    );
  }

  if (phase === "cash") {
    return (
      <div className="max-w-lg mx-auto p-4 pt-10 pb-28" data-testid="register-cash">
        <div className="brutal p-6 text-center">
          <CheckCircle2 size={56} className="mx-auto text-[#FFB800] mb-3" />
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Registration Received</h1>
          <p className="font-display mt-2 text-gray-700">
            {summary?.full_name ? `${summary.full_name}, we've` : "We've"} got your entry on file.
          </p>
          {summary?.divisions && (
            <div className="brutal-2 mt-4 p-3 text-left">
              <div className="font-display text-xs uppercase tracking-widest text-gray-500">Entered divisions</div>
              <ul className="mt-1">
                {summary.divisions.map((d) => <li key={d} className="font-display font-bold uppercase">{d}</li>)}
              </ul>
              <div className="font-display text-sm mt-2">Amount due (cash): <strong>${Number(summary.amount_due ?? 0).toFixed(2)}</strong></div>
            </div>
          )}
          {summary?.waitlist_divisions?.length > 0 && (
            <div className="brutal-2 bg-[#FFF3BF] p-3 mt-4 text-left" data-testid="confirm-waitlist-note">
              <div className="font-display text-xs uppercase tracking-widest text-[#B45309]">Also on the waitlist (no charge)</div>
              <ul className="mt-1">{summary.waitlist_divisions.map((d) => <li key={d} className="font-display font-bold uppercase">{d}</li>)}</ul>
              <p className="font-display text-[11px] mt-1">These are full — we'll place you if a spot opens.</p>
            </div>
          )}
          <div className="brutal-2 bg-[#FFD700] p-3 mt-4 text-left" data-testid="cash-warning">
            <p className="font-display text-sm font-bold uppercase tracking-wide">⚠️ Your spot is not confirmed yet</p>
            <p className="font-display text-sm mt-1">
              Pay at check-in or get it to <strong>FarmKineThings</strong> as soon as possible. Your spot is <strong>NOT confirmed until payment is received</strong> — you won't be placed in a heat until then, and if the heats fill up you could <strong>lose your standing</strong>.
            </p>
            {summary?.needs_sponsorship && (
              <p className="font-display text-sm mt-2">You requested a sponsorship — we'll do our best, but there's <strong>no guarantee</strong>. You'll stay in the holding area until confirmed.</p>
            )}
          </div>
          <button
            data-testid="register-another"
            onClick={() => { setSummary(null); setPhase("form"); setF({ ...f, first_name: "", last_name: "", dob: "", division_ids: [], waiver_accepted: false, signature: "", guardian_name: "", guardian_relationship: "", guardian_phone: "", guardian_signature: "", guardian_consent: false, needs_sponsorship: false }); }}
            className="mt-5 brutal bg-black text-white font-display font-black uppercase tracking-widest px-6 py-3"
          >
            Register Another Competitor
          </button>
        </div>
      </div>
    );
  }

  if (phase === "sponsor_requested") {
    return (
      <div className="max-w-lg mx-auto p-4 pt-10 pb-28" data-testid="register-sponsor-requested">
        <div className="brutal p-6 text-center">
          <Heart size={56} className="mx-auto text-[#E63946] mb-3" />
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Sponsorship Requested</h1>
          <p className="font-display mt-2 text-gray-700">
            {summary?.full_name ? `${summary.full_name}, your` : "Your"} request is in. An organizer will review it and confirm your spot from the community fund.
          </p>
          {summary?.divisions && (
            <div className="brutal-2 mt-4 p-3 text-left">
              <div className="font-display text-xs uppercase tracking-widest text-gray-500">Requested divisions</div>
              <ul className="mt-1">
                {summary.divisions.map((d) => <li key={d} className="font-display font-bold uppercase">{d}</li>)}
              </ul>
            </div>
          )}
          <div className="brutal-2 bg-[#FFD700] p-3 mt-4 text-left">
            <p className="font-display text-sm font-bold uppercase tracking-wide">What happens next</p>
            <p className="font-display text-sm mt-1">
              Sponsorships are limited and approved by an organizer. You'll be <strong>placed in a heat once your sponsorship is approved</strong>. If funds run out, we'll reach out about paying the entry fee — no worries either way.
            </p>
          </div>
          <button
            data-testid="register-another"
            onClick={() => { setSummary(null); setPhase("form"); setF({ ...f, first_name: "", last_name: "", dob: "", division_ids: [], waiver_accepted: false, signature: "", guardian_name: "", guardian_relationship: "", guardian_phone: "", guardian_signature: "", guardian_consent: false, needs_sponsorship: false, sponsorship_reason: "" }); }}
            className="mt-5 brutal bg-black text-white font-display font-black uppercase tracking-widest px-6 py-3"
          >
            Register Another Competitor
          </button>
        </div>
      </div>
    );
  }

  if (phase === "waitlist") {
    return (
      <div className="max-w-lg mx-auto p-4 pt-10 pb-28" data-testid="register-waitlist">
        <div className="brutal p-6 text-center">
          <CheckCircle2 size={56} className="mx-auto text-[#FFB800] mb-3" />
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">You're On The Waitlist</h1>
          <p className="font-display mt-2 text-gray-700">
            {summary?.full_name ? `${summary.full_name}, the` : "The"} division{(summary?.waitlist_divisions?.length || 0) === 1 ? "" : "s"} you picked {(summary?.waitlist_divisions?.length || 0) === 1 ? "is" : "are"} currently full — but you're in line.
          </p>
          {summary?.waitlist_divisions?.length > 0 && (
            <div className="brutal-2 mt-4 p-3 text-left">
              <div className="font-display text-xs uppercase tracking-widest text-gray-500">Waitlisted for</div>
              <ul className="mt-1">
                {summary.waitlist_divisions.map((d) => <li key={d} className="font-display font-bold uppercase">{d}</li>)}
              </ul>
            </div>
          )}
          <div className="brutal-2 bg-[#FFD700] p-3 mt-4 text-left" data-testid="waitlist-note">
            <p className="font-display text-sm font-bold uppercase tracking-wide">What happens next</p>
            <p className="font-display text-sm mt-1">
              <strong>No payment is needed right now.</strong> If a spot opens up, an organizer will place you and settle the entry fee in person. We emailed you a confirmation — mahalo for your patience!
            </p>
          </div>
          <button
            data-testid="register-another"
            onClick={() => { setSummary(null); setPhase("form"); setF({ ...f, first_name: "", last_name: "", dob: "", division_ids: [], waiver_accepted: false, signature: "", guardian_name: "", guardian_relationship: "", guardian_phone: "", guardian_signature: "", guardian_consent: false, needs_sponsorship: false }); }}
            className="mt-5 brutal bg-black text-white font-display font-black uppercase tracking-widest px-6 py-3"
          >
            Register Another Competitor
          </button>
        </div>
      </div>
    );
  }

  if (phase === "success") {
    return (
      <div className="max-w-lg mx-auto p-4 pt-10 pb-28" data-testid="register-success">
        <div className="brutal p-6 text-center">
          <CheckCircle2 size={56} className="mx-auto text-green-600 mb-3" />
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">You're In!</h1>
          <p className="font-display mt-2 text-gray-700">
            {summary?.full_name ? `${summary.full_name}, your` : "Your"} registration is confirmed{summary?.covered ? "" : " & paid"}.
          </p>
          {summary?.divisions && (
            <div className="brutal-2 mt-4 p-3 text-left">
              <div className="font-display text-xs uppercase tracking-widest text-gray-500">Entered divisions</div>
              <ul className="mt-1">
                {summary.divisions.map((d) => (
                  <li key={d} className="font-display font-bold uppercase">{d}</li>
                ))}
              </ul>
              {summary.sponsorship_applied > 0 && (
                <div className="font-display text-sm mt-2 text-green-700">Sponsorship applied: <strong>-${Number(summary.sponsorship_applied).toFixed(2)}</strong></div>
              )}
              <div className="font-display text-sm mt-1">
                {summary.covered
                  ? <span className="text-green-700 font-bold">Entry fully covered by sponsorship — mahalo to our donors! 🤙</span>
                  : <>Paid: <strong>${Number(summary.amount_paid ?? summary.amount_due ?? 0).toFixed(2)}</strong></>}
              </div>
            </div>
          )}
          <p className="font-display text-sm text-gray-600 mt-4">
            Holding period: <strong>first &amp; second weekends of September 2026</strong>. Heat draws will be posted on the Draw page once seeding closes. See you at Pounders!
          </p>
          {myHeats.length > 0 && (
            <div className="brutal-2 mt-4 p-3 text-left bg-[#FFD700]" data-testid="my-heats">
              <div className="font-display text-xs uppercase tracking-widest">Your heat{myHeats.length > 1 ? "s" : ""}</div>
              {myHeats.map((h, i) => (
                <div key={i} className="font-display font-bold uppercase mt-1">
                  {h.division_name} · {h.round_name} · {h.heat_name} — <span className="capitalize">{h.jersey}</span> jersey
                </div>
              ))}
              <p className="font-display text-[11px] normal-case font-normal mt-1">Heats update as more people register — check the Draw tab anytime.</p>
            </div>
          )}
          <button
            data-testid="register-another"
            onClick={() => { setSummary(null); setPhase("form"); setF({ ...f, first_name: "", last_name: "", dob: "", division_ids: [], waiver_accepted: false, signature: "", guardian_name: "", guardian_relationship: "", guardian_phone: "", guardian_signature: "", guardian_consent: false }); }}
            className="mt-5 brutal bg-black text-white font-display font-black uppercase tracking-widest px-6 py-3"
          >
            Register Another Competitor
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto p-4 pb-28" data-testid="register-view">
      <h1 className="font-display text-4xl font-black uppercase tracking-tight mb-1">Register</h1>
      <p className="font-display text-gray-500 uppercase text-xs tracking-widest mb-2">Pounders Fest · Pounders Beach</p>
      <div className="brutal-2 bg-[#FFD700] p-3 mb-3">
        <p className="font-display font-bold uppercase text-sm tracking-wide">
          Holding period: first &amp; second weekends of September 2026
        </p>
        <p className="font-display text-xs mt-1">Entry fee: <strong>$25 per division</strong> · paid securely at the end of registration.</p>
      </div>
      <Link to="/sponsor" data-testid="reg-sponsor-link" className="brutal-2 bg-black text-white p-3 mb-5 flex items-center gap-2 justify-center font-display font-black uppercase text-sm tracking-widest active:scale-[0.99] transition-transform">
        <Heart size={16} /> Sponsor a Kid's Entry
      </Link>

      {error && (
        <div className="brutal-2 bg-[#E63946] text-white p-3 mb-4 flex items-center gap-2" data-testid="register-error">
          <AlertCircle size={18} /> <span className="font-display font-bold text-sm">{error}</span>
        </div>
      )}

      {/* Competitor */}
      <div className="brutal p-4 mb-5">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-3">Competitor</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" required>
            <input data-testid="reg-first-name" className={inputCls} value={f.first_name} onChange={(e) => set("first_name", e.target.value)} />
          </Field>
          <Field label="Last name" required>
            <input data-testid="reg-last-name" className={inputCls} value={f.last_name} onChange={(e) => set("last_name", e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date of birth" required>
            <input data-testid="reg-dob" type="date" className={inputCls} value={f.dob} onChange={(e) => set("dob", e.target.value)} />
          </Field>
          <Field label="Gender" required>
            <select data-testid="reg-gender" className={inputCls} value={f.gender} onChange={(e) => set("gender", e.target.value)}>
              <option value="">Select…</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </Field>
        </div>
        {age != null && (
          <div className="font-display text-sm mb-3" data-testid="reg-age">
            Age on contest date: <strong className="font-score text-2xl">{age}</strong>
            {isMinor && <span className="ml-2 bg-[#E63946] text-white text-[10px] font-black uppercase px-2 py-0.5">Under 18 — guardian consent required</span>}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email" required>
            <input data-testid="reg-email" type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} />
          </Field>
          <Field label="Phone" required>
            <input data-testid="reg-phone" className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} />
          </Field>
        </div>
        <Field label="T-shirt size" required>
          <select data-testid="reg-shirt" className={inputCls} value={f.shirt_size} onChange={(e) => set("shirt_size", e.target.value)}>
            <option value="">Select…</option>
            {SHIRT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>

      {/* Emergency contact */}
      <div className="brutal p-4 mb-5">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-3">Emergency Contact</h2>
        <Field label="Contact name" required>
          <input data-testid="reg-ec-name" className={inputCls} value={f.emergency_contact_name} onChange={(e) => set("emergency_contact_name", e.target.value)} />
        </Field>
        <Field label="Contact phone" required>
          <input data-testid="reg-ec-phone" className={inputCls} value={f.emergency_contact_phone} onChange={(e) => set("emergency_contact_phone", e.target.value)} />
        </Field>
      </div>

      {/* Divisions */}
      <div className="brutal p-4 mb-5">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-1">Divisions</h2>
        <p className="font-display text-xs text-gray-500 mb-3">Pick every division you want to enter (you can enter more than one).</p>
        <div className="space-y-2" data-testid="reg-divisions">
          {divisions.map((d) => {
            const el = eligibility(d.name, age, f.gender);
            const isFull = !!d.full;
            const canPick = el.ok;
            const selected = f.division_ids.includes(d.id);
            const spotsNote = isFull
              ? "FULL — join the waitlist (no payment now)"
              : (d.spots_left != null ? `${d.spots_left} spot${d.spots_left === 1 ? "" : "s"} left` : null);
            return (
              <button
                key={d.id}
                type="button"
                disabled={!canPick}
                data-testid={`reg-division-${d.id}`}
                onClick={() => canPick && toggleDiv(d.id)}
                className={`w-full brutal-2 p-3 flex items-center justify-between text-left transition-transform ${
                  selected ? "bg-black text-white" : "bg-white"
                } ${!canPick ? "opacity-40 cursor-not-allowed" : "active:scale-[0.99]"}`}
              >
                <div>
                  <div className="font-display font-black uppercase flex items-center gap-2">
                    {d.name}
                    {isFull && <span className="bg-[#FFB800] text-black text-[9px] font-black uppercase px-1.5 py-0.5" data-testid={`reg-division-full-${d.id}`}>Waitlist</span>}
                  </div>
                  <div className={`font-display text-[11px] uppercase tracking-widest ${selected ? "text-gray-300" : "text-gray-500"}`}>{el.reason}</div>
                  {el.ok && spotsNote && (
                    <div className={`font-display text-[11px] uppercase tracking-widest font-bold ${isFull ? "text-[#B45309]" : (selected ? "text-[#FFD700]" : "text-green-700")}`}>{spotsNote}</div>
                  )}
                </div>
                <div className={`w-6 h-6 border-4 ${selected ? "bg-[#FFD700] border-[#FFD700]" : "border-black"}`}>
                  {selected && <CheckCircle2 size={16} className="text-black" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Sponsorship request (17 & under only) */}
      {age != null && age <= 17 && (
      <div className="brutal p-4 mb-5" data-testid="reg-sponsorship">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-1">Need Help With The Entry Fee?</h2>
        <p className="font-display text-xs text-gray-600 mb-3">
          Our community fund helps keiki who normally couldn't enter due to cost. Sponsorships are limited and <strong>approved by an organizer</strong> — if you can afford the entry, please pay so the fund reaches the kids who need it most.
          {fund != null && (
            <span className="block mt-1 font-bold text-green-700">
              Community fund available right now: ${Number(fund.available).toFixed(2)}
            </span>
          )}
        </p>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            data-testid="reg-needs-sponsorship"
            type="checkbox"
            className="mt-1 w-5 h-5 accent-black"
            checked={f.needs_sponsorship}
            onChange={(e) => set("needs_sponsorship", e.target.checked)}
          />
          <span className="font-display text-sm font-bold">
            I'd like to apply for a sponsorship to help cover this entry fee.
          </span>
        </label>
        {f.needs_sponsorship && (
          <div className="mt-3" data-testid="reg-sponsorship-reason-wrap">
            <Field label="Share a short story — why are you applying? (organizers read this)" required>
              <textarea
                data-testid="reg-sponsorship-reason"
                rows={5}
                className={inputCls}
                value={f.sponsorship_reason}
                onChange={(e) => set("sponsorship_reason", e.target.value)}
                placeholder="Tell us your story — what bodyboarding means to you, why the entry fee is tough for your ohana right now, and anything that helps us understand. A few sentences is perfect."
              />
              <span className="font-display text-[11px] text-gray-400">{f.sponsorship_reason.trim().length}/20 characters minimum</span>
            </Field>
            <p className="font-display text-xs text-gray-600">
              Submitting this will place your entry in a <strong>request queue</strong> — no payment is taken now. An organizer will read your story and confirm your spot from the fund.
            </p>
          </div>
        )}
      </div>
      )}

      {/* Guardian (minors) */}
      {isMinor && (
        <div className="brutal p-4 mb-5 border-[#E63946]" data-testid="reg-guardian">
          <h2 className="font-display text-xl font-black uppercase tracking-tight mb-1">Parent / Guardian Consent</h2>
          <p className="font-display text-xs text-gray-600 mb-3">Required because the competitor is under 18.</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Guardian name" required>
              <input data-testid="reg-guardian-name" className={inputCls} value={f.guardian_name} onChange={(e) => set("guardian_name", e.target.value)} />
            </Field>
            <Field label="Relationship" required>
              <input data-testid="reg-guardian-rel" className={inputCls} value={f.guardian_relationship} onChange={(e) => set("guardian_relationship", e.target.value)} placeholder="e.g. Mother" />
            </Field>
          </div>
          <Field label="Guardian phone" required>
            <input data-testid="reg-guardian-phone" className={inputCls} value={f.guardian_phone} onChange={(e) => set("guardian_phone", e.target.value)} />
          </Field>
          <label className="flex items-start gap-3 mt-2 cursor-pointer">
            <input data-testid="reg-guardian-consent" type="checkbox" className="mt-1 w-5 h-5 accent-black" checked={f.guardian_consent} onChange={(e) => set("guardian_consent", e.target.checked)} />
            <span className="font-display text-sm">
              As the parent/legal guardian, I consent to my child competing and I agree to the Waiver &amp; Release of Liability below on their behalf.
            </span>
          </label>
          <Field label="Guardian signature (type full name)" required>
            <input data-testid="reg-guardian-signature" className={`${inputCls} italic`} value={f.guardian_signature} onChange={(e) => set("guardian_signature", e.target.value)} placeholder="Parent/guardian full name" />
          </Field>
        </div>
      )}

      {/* Payment method */}
      {!requestingSponsorship && !allWaitlist && (
      <div className="brutal p-4 mb-5" data-testid="reg-payment-method">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-3">How Will You Pay?</h2>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            data-testid="pay-online"
            onClick={() => set("payment_method", "online")}
            className={`brutal-2 p-3 font-display font-black uppercase text-sm active:scale-[0.99] transition-transform ${f.payment_method === "online" ? "bg-black text-white" : "bg-white"}`}
          >
            Pay Online<br /><span className="font-normal text-[11px] normal-case">Card · via Bluevine</span>
          </button>
          <button
            type="button"
            data-testid="pay-cash"
            onClick={() => set("payment_method", "cash")}
            className={`brutal-2 p-3 font-display font-black uppercase text-sm active:scale-[0.99] transition-transform ${f.payment_method === "cash" ? "bg-black text-white" : "bg-white"}`}
          >
            Pay In Person<br /><span className="font-normal text-[11px] normal-case">Cash · admin confirms</span>
          </button>
        </div>
        {f.payment_method === "cash" && (
          <p className="font-display text-xs text-[#E63946] mt-2" data-testid="cash-warning-inline">
            <strong>Your spot is NOT confirmed until payment is received.</strong> Pay at check-in or to FarmKineThings ASAP. You won't be placed in a heat until payment is received — and if the heats fill up, you could lose your standing.
          </p>
        )}
        {f.payment_method === "online" && (
          <p className="font-display text-xs text-gray-600 mt-2" data-testid="online-note-inline">
            You'll pay via our secure <strong>Bluevine</strong> link on the next step. Your spot is confirmed once an organizer verifies your payment.
          </p>
        )}
      </div>
      )}

      {/* Waiver */}
      <div className="brutal p-4 mb-5">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-3">Waiver &amp; Release of Liability</h2>
        <div className="brutal-2 bg-gray-50 p-3 max-h-64 overflow-y-auto text-sm leading-relaxed font-display text-gray-800 space-y-3" data-testid="waiver-text">
          <p><strong>Pounders Fest Bodyboarding Contest.</strong> Holding period: the <strong>first and second weekends of September 2026</strong> (exact competition day within this window to be called based on surf conditions).</p>
          <p><strong>Assumption of Risk.</strong> I understand that bodyboarding and ocean activities are inherently dangerous and involve risk of serious injury, drowning, permanent disability, or death, whether caused by my own actions, the actions of others, the conditions of the ocean and beach, or otherwise. I knowingly and voluntarily accept and assume all such risks and choose to participate.</p>
          <p><strong>Release &amp; Waiver.</strong> In consideration of being permitted to participate, I, on behalf of myself, my heirs, and next of kin, hereby release, waive, and hold harmless <strong>FarmKineThings</strong>, and its organizers, directors, officers, volunteers, judges, sponsors, and the property/beach owners, from any and all liability, claims, or demands for any injury, illness, death, or loss/damage to property that I (or my child) may suffer arising from participation in this event, <strong>even if caused by negligence.</strong> FarmKineThings will not be held liable for anyone who is injured or hurt in connection with this event.</p>
          <p><strong>Medical Authorization.</strong> I authorize event staff to obtain emergency medical treatment if needed and I am responsible for any resulting costs.</p>
          <p><strong>Media Release.</strong> I grant permission for photos/video taken at the event to be used for promotional purposes without compensation.</p>
          <p><strong>Rules.</strong> I agree to follow all event rules, the directions of judges, water safety, and organizers, and confirm the competitor is physically fit to participate.</p>
        </div>
        <label className="flex items-start gap-3 mt-3 cursor-pointer">
          <input data-testid="reg-waiver-accept" type="checkbox" className="mt-1 w-5 h-5 accent-black" checked={f.waiver_accepted} onChange={(e) => set("waiver_accepted", e.target.checked)} />
          <span className="font-display text-sm font-bold">
            I have read and agree to the Waiver &amp; Release of Liability above{isMinor ? " (as parent/guardian on behalf of the minor competitor)" : ""}.
          </span>
        </label>
        <Field label={isMinor ? "Competitor / signer name (type full name)" : "Signature (type full name)"} required>
          <input data-testid="reg-signature" className={`${inputCls} italic`} value={f.signature} onChange={(e) => set("signature", e.target.value)} placeholder="Type your full name" />
        </Field>
      </div>

      <button
        data-testid="reg-submit"
        onClick={submit}
        disabled={submitting}
        className="w-full brutal bg-[#E63946] text-white font-display font-black uppercase tracking-widest py-5 text-lg active:scale-[0.99] transition-transform disabled:opacity-60"
      >
        {submitting
          ? (requestingSponsorship ? "Submitting request…" : (allWaitlist ? "Joining waitlist…" : "Submitting…"))
          : (allWaitlist ? "Join The Waitlist" : (requestingSponsorship ? "Submit Sponsorship Request" : (f.payment_method === "cash" ? `Submit · Pay $${feeTotal} In Person` : `Continue to Payment · $${feeTotal}`)))}
      </button>
      <p className="font-display text-xs text-gray-500 text-center mt-2">
        {allWaitlist
          ? "All picked divisions are full — you'll join the waitlist. No payment now; an organizer places you if a spot opens."
          : requestingSponsorship
          ? "No payment now — your entry is held until an organizer approves your sponsorship."
          : <>{payingCount} division{payingCount === 1 ? "" : "s"} × $25 = ${feeTotal}.
            {selectedFull.length > 0 ? ` (+${selectedFull.length} full → waitlist, no charge)` : ""}
            {f.payment_method === "cash" ? " Held until cash payment is confirmed." : " Pay online next — held until we confirm your payment."}</>}
      </p>
    </div>
  );
}
